import React, { Suspense, useEffect, useRef, useState } from 'react';
import {
    ContactUs,
    FooterContent,
    FooterItem,
    FooterLeft,
    FooterRights,
    FooterWrapper,
    LanguageSelectorWrapper,
    LinkedFooterItem,
} from './Footer.style';
import { LanguageSelector, LanguageSelectorRef } from './LanguageSelector';
import { getData } from '../../common/services/network.service';
import { cookieNames, setCookie } from '../../common/services/cookies.service';
import { Theme } from '../../common/theme/Theme';
import { useEvent } from '../../common/hooks/useEvent.hook';
import { contactUsModalEvents } from '../../common/events/contactUsModal.events';
import { ContactUsFormHelpOptions, FormType } from '../../contactUs/data/constants';
import { stringFormat } from '../../../utils/checkEmailValidation';
import userDataEvents from '../../common/events/userData.events';
import { cookieUrls, footerUrls } from '../../../constants';
import { Loader } from '@toluna-ui-toolkit/loader';
import { Modal } from '../../modal/components/Modal';
import { CloseButton } from '../../contactUs/components/ContactUs.style';
import footerDataEvents from '../../../utils/events/footer.events';
import { convertToWwwUrl, createSolutionRoute } from '../../../utils/url.service';
import { InitOptions } from '../../common/models/common.models';
import { ContactUsFormData } from '../../contactUs/data/models';
import { Culture, FooterData, Industry } from '../data/models';
import { isSpecialTranslationCulture } from '../../common/services/translations.service';
import useData from 'shell/dataContext';
import { FooterSkeleton } from './FooterSkeleton';
import { getUrlParam } from '../../breadcrumbs/data/utils';
import { ExcludeLanguageSelectorWithTemplateIdList, URL_TEMPLATE_ID } from '../../breadcrumbs/data/constants';
import breadcrumbsDataEvents from '../../common/events/breadcrumbs.events';

const ContactUsModal = React.lazy(
    () => import(/* webpackChunkName: "ContactUs.modal" */ '../../contactUs/components/ContactUs.Modal')
);

export interface IFooterProps extends InitOptions {
    onSelectLanguage?: (langId: number | undefined) => void;
    onLoad?: Function;
    onLoadError?: (err: Error) => void;
    isNewBrand: boolean;
    id?: string;
}

export interface IFooterLinkProps {
    link: string;
    text?: string;
    isHosted: boolean;
}

const FooterLink = ({ isHosted, link, text }: IFooterLinkProps) => {
    return isHosted ? (
        <LinkedFooterItem to={createSolutionRoute(link)} tabIndex={0}>
            {text}
        </LinkedFooterItem>
    ) : (
        <FooterItem href={link} tabIndex={0}></FooterItem>
    );
};

export const Footer = (props: IFooterProps) => {
    const { onSelectLanguage, baseUrl, onLoad, onLoadError, cookieDomain, isNewBrand } = props;
    const [isLoaded, setIsLoaded] = useState(false);
    const [footerData, setFooterData] = useState<Partial<FooterData>>({});
    const [selectedCulture, setSelectedCulture] = useState('');
    const [showContactUsModal, setShowContactUsModal] = useState(false);
    const [selectedCultureObj, setSelectedCultureObj] = useState<Partial<Culture>>({});
    const [formData, setFormData] = useState<Partial<ContactUsFormData>>({});
    const DEFAULT_CULTURE_ID = 1;
    const { isHosted } = useData() ?? {};
    const languageSelectorRef = useRef<LanguageSelectorRef | null>(null);
    const languageWrapperRef = useRef<HTMLDivElement | null>(null);
    let showLanguageSelector = true;
    const templateId = getUrlParam(URL_TEMPLATE_ID);
    if (templateId && ExcludeLanguageSelectorWithTemplateIdList.includes(templateId)){
        showLanguageSelector = false;
    }
    useEffect(() => {
        if (baseUrl) {
            initComponent();
        }

        return () => {
            if (window?.tcp?.user) {
                // @ts-ignore
                window.tcp.user.email = undefined;
                window.tcp.user.userDetailsTraits = undefined;
                // @ts-ignore
                window.tcp.user.licenseType = undefined;
            }
        };
    }, [baseUrl]);

    const transformFooterData = (data: FooterData): FooterData => {
        return {
            ...data,
            tiuiDomain: convertToWwwUrl(data.tiuiDomain),
        };
    };

    const initComponent = () => {
        getData(
            `${baseUrl}ContentProvider/GetFooterInitialData`,
            response => {
                setFooterData(transformFooterData(response));
                setFormData({
                    userData: response.user,
                    translations: response.translations,
                    helpDescription: '',
                    selectedCategoryId: undefined,
                    customOptionsList: undefined,
                });
                const { supportedCultures, selectedCulture: responseCulture } = response;
                const selectedSupportedCulture =
                    supportedCultures.find((culture: Culture) => culture.id === responseCulture) ||
                    supportedCultures.find((culture: Culture) => culture.id === DEFAULT_CULTURE_ID);

                setSelectedCulture(selectedSupportedCulture.shortName[0] + selectedSupportedCulture.shortName[1]);
                setSelectedCultureObj(selectedSupportedCulture);
                setIsLoaded(true);
                userDataEvents.sendUserData(response.user);
                footerDataEvents.getFooterData({ selectedLanguage: responseCulture });

                window.tcp.user = {
                    ...window.tcp.user,
                    email: response.user?.email,
                    userDetailsTraits: response.user?.userDetailsTraits,
                    licenseType: response.user?.licenseType,
                    userId: response.user?.userId,
                    isInternalUser: response.user?.isInternalUser ?? false,
                };

                onLoad && onLoad();
                breadcrumbsDataEvents.initData();
            },
            error => {
                onLoadError && onLoadError(error);
            }
        );
    };

    const loadIndustries = () => {
        getData(
            `${baseUrl}home/GetRegistrationInitialData`,
            response => {
                setFormData({
                    ...formData,
                    industries: response.industries.map((i: Industry) => ({ value: i.id, label: i.name })),
                });
                setIsLoaded(true);
                onLoad && onLoad();
            },
            error => {
                onLoadError && onLoadError(error);
            }
        );
    };

    const handleSelectLanguage = (language: any) => {
        if (onSelectLanguage) {
            onSelectLanguage(language.id);
        } else {
            setCookie(cookieNames.USER_CULTURE, language?.id, 14, cookieDomain);
            setCookie(cookieNames.WALKME_CULTURE, language.shortName, 14, cookieDomain);
            window.location.reload();
        }
    };

    const handleKeyDownContactUs = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            renderContactUsModalOnMouseClick();
        }
    };

    const handleKeyDownLanguageSelector = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (languageSelectorRef.current) {
                languageSelectorRef.current?.toggleLanguageMenu();
            }
        }
    };

    const contactUsRef = useRef<HTMLDivElement | null>(null);

    const getLinks = () => {
        return [
            <FooterLink
                key={'our-licenses'}
                link={`${footerData.tiuiDomain}pricing`}
                text={footerData.translations?.TI2020_ContentTranslations_Footer_OurLicenses}
                isHosted={isHosted}
            />,
            <FooterLink
                key={'terms-and-conditions'}
                link={`${footerData.tiuiDomain}terms-and-conditions`}
                text={footerData.translations?.TI2020_ContentTranslations_Footer_TermsConditions}
                isHosted={isHosted}
            />,
            <FooterLink
                key={'standard-contractual-clauses'}
                link={`${footerData.tiuiDomain}standard-contractual-clauses`}
                text={footerData.translations?.TI2020_ContentTranslations_Footer_SCC}
                isHosted={isHosted}
            />,
            <FooterLink
                key={'privacy-policy'}
                link={`${footerData.tiuiDomain}privacy-policy`}
                text={footerData.translations?.TI2020_ContentTranslations_Footer_PrivacyPolicy}
                isHosted={isHosted}
            />,
            <FooterItem
                key={'cookie-policy'}
                target="_blank"
                href={getCookiePolicyLink(selectedCulture) || '#'}
                tabIndex={0}
            >
                {footerData.translations?.TI2020_ContentTranslations_Footer_CookiesPolicy}
            </FooterItem>,
            <ContactUs
                key={'contact-us'}
                id="footer-contact-us"
                ref={contactUsRef}
                onClick={renderContactUsModalOnMouseClick}
                tabIndex={0}
                onKeyDown={handleKeyDownContactUs}
            >
                {footerData.translations?.TI2020_ContentTranslations_Footer_ContactUs}
            </ContactUs>,
            <FooterItem
                key={'learn-more'}
                target="_blank"
                rel="noopener noreferrer"
                href={footerUrls.SOLUTIONS || '#'}
                tabIndex={0}
            >
                {footerData.translations?.TI2020_ContentTranslations_Footer_LearnMore}
            </FooterItem>,
            <FooterItem
                key={'support-updates'}
                target="_blank"
                rel="noopener noreferrer"
                href={footerUrls.SUPPORT_UPDATES || '#'}
                tabIndex={0}
            >
                {footerData.translations?.TI2020_ContentTranslations_Footer_SupportUpdates}
            </FooterItem>,
        ];
    };

    const getOrderedCultures = () => {
        return (
            footerData &&
            footerData.supportedCultures &&
            footerData.supportedCultures.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
        );
    };

    const getCookiePolicyLink = (culture: string) => {
        switch (culture.toLowerCase()) {
            case 'de': {
                return cookieUrls.DE;
            }
            case 'fr': {
                return cookieUrls.FR;
            }
            case 'pt': {
                return cookieUrls.PT;
            }
            default:
                return cookieUrls.DEFAULT(culture);
        }
    };

    const getPrefillContactUsFormDataByType = (formType: FormType, customFormOptions: any) => {
        switch (formType) {
            case FormType.ADVANCED_LICENSE:
                return {
                    selectedCategoryId: ContactUsFormHelpOptions.REQUEST_ADVANCED_LICENSE,
                    helpDescription: formData?.translations?.TI2020_ContentTranslations_Footer_ContactUs_Advanced,
                    customOptionsList: null,
                };
            case FormType.STANDARD_LICENSE:
                return {
                    selectedCategoryId: ContactUsFormHelpOptions.REQUEST_STANDARD_LICENSE,
                    helpDescription:
                        formData?.translations?.TI2020_ContentTranslations_Footer_ContactUs_StandardLicense,
                    customOptionsList: null,
                };
            case FormType.ASSISTED_SOLUTIONS:
                return {
                    selectedCategoryId: ContactUsFormHelpOptions.OPTION_11,
                    helpDescription: '',
                    customOptionsList: null,
                };
            case FormType.TOLUNASTART_QUAL:
                return {
                    selectedCategoryId: ContactUsFormHelpOptions.TOLUNA_START_QUAL,
                    helpDescription: '',
                    customOptionsList: null,
                };
            case FormType.AD_REVIEW:
                return {
                    selectedCategoryId: ContactUsFormHelpOptions.AD_REVIEW,
                    helpDescription: '',
                    customOptionsList: null,
                };
            case FormType.CUSTOM:
                return {
                    ...customFormOptions,
                };
            default:
                return {
                    selectedCategoryId: null,
                    helpDescription: '',
                    customOptionsList: null,
                };
        }
    };

    const renderContactUsModal = async (evt: any = null) => {
        await loadIndustries();
        setFormData({
            ...formData,
            ...getPrefillContactUsFormDataByType(evt?.detail.formType, evt?.detail.customFormOptions),
        });
        setShowContactUsModal(true);
    };

    const renderContactUsModalOnEvent = (event: Event) => {
        const customEvent = event as CustomEvent;
        setFormData({
            ...formData,
            ...getPrefillContactUsFormDataByType(customEvent?.detail?.formType, customEvent?.detail?.customFormOptions),
        });
        renderContactUsModal(customEvent);
    };

    const renderContactUsModalOnMouseClick = () => {
        renderContactUsModal();
    };

    useEvent({ name: contactUsModalEvents.SHOW_CONTACT_US, handler: renderContactUsModalOnEvent });
    useEvent({ name: contactUsModalEvents.HIDE_CONTACT_US, handler: () => setShowContactUsModal(false) });

    return (
        <Theme isNewBrand={isNewBrand}>
            {isLoaded ? (
                <FooterWrapper breakLine={isSpecialTranslationCulture()} className="ti-footer-wrapper">
                    {footerData && (
                        <FooterContent>
                            <FooterLeft>
                                <FooterRights>
                                    {stringFormat(
                                        footerData.translations?.TI2020_ContentTranslations_Footer_CopyRight,
                                        new Date().getFullYear()
                                    )}
                                </FooterRights>
                                {getLinks()}
                            </FooterLeft>
                             {(showLanguageSelector && 
                                <LanguageSelectorWrapper
                                    ref={languageWrapperRef}
                                    tabIndex={0}
                                    onKeyDown={handleKeyDownLanguageSelector}
                                >
                                    <LanguageSelector
                                        ref={languageSelectorRef}
                                        supportedLanguages={getOrderedCultures() ?? []}
                                        selectedCulture={selectedCultureObj}
                                        onSelectLanguage={handleSelectLanguage}
                                        handleFocusParent={() => languageWrapperRef.current?.focus()}
                                    />
                                </LanguageSelectorWrapper>
                             )}
                        </FooterContent>
                    )}
                    {footerData.translations && showContactUsModal && (
                        <Suspense
                            fallback={
                                <Modal showModal={true} width={'655px'} height={'620px'}>
                                    <Loader outterColor="#DBDBDB" innerColor="#6A6A6A" />
                                    <CloseButton onClick={() => setShowContactUsModal(false)} />
                                </Modal>
                            }
                        >
                            <ContactUsModal
                                formData={formData as ContactUsFormData}
                                contactUsRef={contactUsRef}
                                showModal={true}
                                closeModal={() => setShowContactUsModal(false)}
                                baseUrl={baseUrl}
                            />
                        </Suspense>
                    )}
                </FooterWrapper>
            ) : (
                <FooterSkeleton />
            )}
        </Theme>
    );
};

Footer.defaultProps = {
    options: {
        advancedFeatures: {
            renderIndications: true,
        },
    },
    isNewBrand: true,
};
